/**
 * The integration links on one variant row: Trigger rules, Slack overrides
 * and Pipeline. Each opens the same drawer the old Integrations tab's
 * per-variant lists opened, so a variant's own settings sit on its row
 * instead of under the app.
 *
 * They follow the old lists' rule for showing: Trigger rules only when the
 * workspace has GitHub connected, Slack overrides only when it has Slack
 * connected, and Pipeline always, since that needs no workspace connection.
 * Counts come from the same `oversight` rows those lists read.
 */
import { Button } from '@/components/ui'
import { useAppIntegrations } from './useAppIntegrations'

/** "2 rules", "1 override", or the "none yet" wording for zero. */
function countLabel(count: number, one: string, many: string): string {
  if (count === 0) return 'none yet'
  return `${count} ${count === 1 ? one : many}`
}

export function VariantIntegrationLinks({ variantId, variantName }: { variantId: string; variantName: string }) {
  const { data, openDrawer } = useAppIntegrations()
  // Until the data arrives the links can't say which connections exist,
  // so none show rather than ones that might vanish a moment later.
  if (!data) return null

  const ruleCount = data.oversight.github.find((r) => r.variantId === variantId)?.count ?? 0
  const overrideCount = data.oversight.slack.find((r) => r.variantId === variantId)?.count ?? 0

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border/50 mt-2 pt-2 text-xs text-muted-foreground" data-testid="variant-integration-links">
      {data.github.connected && (
        <span>
          Trigger rules:{' '}
          <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => openDrawer(variantId, 'trigger')} aria-label={`Trigger rules for ${variantName}`}>
            {countLabel(ruleCount, 'rule', 'rules')}
          </Button>
        </span>
      )}
      {data.slack.connected && (
        <span>
          Slack overrides:{' '}
          <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => openDrawer(variantId, 'slack')} aria-label={`Slack overrides for ${variantName}`}>
            {countLabel(overrideCount, 'override', 'overrides')}
          </Button>
        </span>
      )}
      <span>
        Pipeline:{' '}
        <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => openDrawer(variantId, 'pipeline')} aria-label={`Pipeline for ${variantName}`}>
          Configure
        </Button>
      </span>
    </div>
  )
}
